import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Subject } from 'rxjs';
import { TablepageBaseComponent } from '../../abstract/tablepage-base/tablepage-base.component';
import { WorkDialogComponent } from '../../dialogs/work-dialog/work-dialog.component';
import { Work } from '../../models/work';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { StatusDatePipe } from '../../utilities/date-pipes';
import { FilterService } from 'primeng/api';
import { Reset } from '../../utilities/reset';

@Component({
  selector: 'app-workers',
  imports: [
    FormsModule,
    CommonModule,
    TablepageBaseComponent,
    TableModule,
    ButtonModule,
    InputTextModule,
    StatusDatePipe,
    TranslatePipe,
    WorkDialogComponent
  ],
  standalone: true,
  templateUrl: './workers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``,
})
export class WorkersComponent implements OnInit, OnDestroy {
  messageService = inject(UiNotificationService);
  private filterService = inject(FilterService);
  private translate = inject(TranslateService);

  loading: boolean = false;
  showDialog: boolean = false;
  dtTrigger: Subject<boolean> = new Subject<boolean>();

  works: Work[] = [];
  newWork: Work = Reset.work();
  selectedWork: Work = Reset.work();
  isNew: boolean = false;

  constructor(private apiService: ApiService) {
  }

  ngOnInit(): void {
    // Registriamo un filtro intelligente unico per la ricerca globale
        this.filterService.register('smartGlobalFilter', (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null || String(filter).trim() === '') {
        return true;
      }
      if (value === undefined || value === null) {
        return false;
      }

      const searchString = String(filter).toLowerCase();
      const stringValue = String(value);

      // 1. Verifica se il valore è una chiave di traduzione valida presente nei tuoi file JSON.
      // ngx-translate restituisce la chiave stessa se la traduzione manca.
      const translatedValue = this.translate.instant(stringValue);

      if (translatedValue !== stringValue) {
        // Il campo è tradotto (es. 'name'): effettua il match sulla stringa tradotta
        return translatedValue.toLowerCase().includes(searchString);
      } else {
        // Il campo non è tradotto (es. 'organization'): effettua il match sul valore originale
        return stringValue.toLowerCase().includes(searchString);
      }
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  getFrequencyString(work: Work): string {
    if (work.deltaDays === 0) {
      const [h, m, s] = work.deltaTime.split(':').map(Number);
      if (h === 0 && m > 0) {
        return this.translate.instant('frequency.every_x_minutes', { m });
      }

      if (h === 1) {
        if (m === 0) {
          return this.translate.instant('frequency.every_hour');
        } else {
          return this.translate.instant('frequency.every_hour_and_x_minutes', { m });
        }
      }

      if (h > 1) {
        if (m === 0) {
          return this.translate.instant('frequency.every_x_hours', { h });
        } else {
          return this.translate.instant('frequency.every_x_hours_and_y_minutes', { h, m });
        }
      }

      return this.translate.instant('frequency.undefined');
    }

    if (work.deltaDays === 1) {
      return this.translate.instant('frequency.every_day', { time: work.deltaTime });
    }

    return this.translate.instant('frequency.every_x_days', { days: work.deltaDays, time: work.deltaTime });
  }

  onShowDialog(job: Work): void {
    this.isNew = job.id === '';
    this.selectedWork = structuredClone(job);
    this.showDialog = true;
  }

  onWorkSaved(work: Work) {
    this.selectedWork = Reset.work();
    this.loadData();
  }


  private loadData(): void {
    this.loading = true;

    this.apiService.getWorks().subscribe({
      next: (data) => {
        this.works = data;
        this.dtTrigger.next(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.messageService.showError(err);
      },
      complete: () => {
        this.loading = false;
      }
    })
  }
}
